import { useMemo, useState } from 'react';

import { IconCheck, IconTag } from '@tabler/icons-react';

import { Group, Select, SelectProps } from '@optiro/ui';

import { useCategories } from '@/hooks/useCategories';
import { useCreateCategory } from '@/hooks/useCreateCategory';
import { ProjectCategory } from '@/modules/Projects/components/Category/Category';
import { Category } from '@/types/types';

type CategorySelectProps = Omit<SelectProps, 'data'> & {
  value: Category['id'] | string;
  onChange: (value: Category['id'] | null) => void;
};

export const SelectCategory = ({ value, onChange, ...props }: CategorySelectProps) => {
  const { data: categories } = useCategories();
  const { mutate: createCategory } = useCreateCategory();
  const [query, setQuery] = useState('');

  const options = useMemo(() => {
    if (!categories) return [];

    return categories.map((category) => ({
      value: String(category.id),
      label: category.name,
    }));
  }, [categories]);

  const handleCreate = (query: string) => {
    createCategory(
      { name: query },
      {
        onSuccess: (data) => {
          setQuery('');
          onChange(Number(data.id));
        },
      }
    );
  };

  const handleChange = (val: string | null) => {
    if (val === '__create__' && query) {
      handleCreate(query);
    } else {
      onChange(Number(val));
    }
  };

  const searchableOptions = useMemo(() => {
    if (!query) return options;

    const match = options.find((opt) => opt.label.toLowerCase() === query.toLowerCase());

    if (!match) {
      return [...options, { value: '__create__', label: `Opret "${query}"` }];
    }

    return options;
  }, [query, options]);

  const renderOption = (option: { value: string; label: string }, checked: boolean | undefined) => {
    return (
      <Group>
        {checked && <IconCheck />}
        {option.value === '__create__' ? (
          option.label
        ) : (
          <ProjectCategory categoryId={Number(option.value)} />
        )}
      </Group>
    );
  };

  return (
    <Select
      label="Kategori"
      placeholder="Vælg eller opret kategori"
      description="Hvis ingen kategori passer, kan du oprette en ny, ved at skrive i feltet. og vælge 'Opret ...'"
      searchable
      data={searchableOptions}
      value={value}
      leftSection={<IconTag size={16} />}
      onChange={handleChange}
      onSearchChange={setQuery}
      renderOption={({ checked, option }) => renderOption(option, checked)}
      {...props}
    />
  );
};
